"use client";

/**
 * lib/use-chunk-reload-recovery.tsx — client wiring for the stale-deploy
 * auto-recovery (lib/chunk-load-recovery.ts): stale JS chunks AND stale
 * Server Action IDs after a deploy ("chunk" in the names is historical).
 * Exports the shared error-boundary hook plus the root-layout
 * unhandled-rejection listener (StaleDeployRejectionRecovery).
 *
 * Next.js error boundaries are nested: app/(app)/error.tsx and the
 * document-generation error.tsx files catch errors BEFORE the root
 * app/error.tsx, so the recovery must run in EVERY boundary (Codex P1 on
 * PR #729). Each error.tsx calls this hook and early-returns
 * <ChunkReloadNotice /> while the reload is in flight:
 *
 *   const { autoReloading, staleDeployError } = useChunkReloadRecovery(error);
 *   ...
 *   if (autoReloading) return <ChunkReloadNotice />;
 *
 * Boundaries must also skip their error-reporting effects while
 * `autoReloading` is true (the transient deploy-skew error heals itself; see
 * the suppression rationale in lib/chunk-load-recovery.ts), and prefer a
 * real `window.location.reload()` over `reset()` when `staleDeployError` is
 * true — reset() re-renders the same tree and cannot re-fetch a renamed
 * chunk or resolve a renamed Server Action ID.
 */
import { useEffect, useState } from "react";
import {
  isStaleDeployError,
  markChunkReloadAttempt,
  recoverFromStaleDeployRejection,
  shouldAutoReloadForStaleDeployError,
} from "@/lib/chunk-load-recovery";

export function useChunkReloadRecovery(error: unknown): {
  /** True when a guarded full-page reload has been scheduled for this error. */
  autoReloading: boolean;
  /**
   * True when the error is a stale-deploy artifact (chunk-load failure or
   * stale Server Action), regardless of the guard.
   */
  staleDeployError: boolean;
} {
  // Decide ONCE on mount (useState initializer — side-effect free, so
  // StrictMode double-invocation / aborted concurrent renders can't burn the
  // one-shot guard before commit; Codex P2 on PR #729). The guard WRITE
  // happens in the committed effect below, and we only reload if the marker
  // VERIFIABLY persisted: if the sessionStorage write fails (quota / private
  // mode / write-blocking policy), an unguarded reload into a persistently
  // missing chunk would loop forever (Codex P1) — fall back to the normal
  // error UI instead, which also un-gates the boundary's reporting effects.
  const [phase, setPhase] = useState<"reloading" | "error-ui">(() =>
    shouldAutoReloadForStaleDeployError(error) ? "reloading" : "error-ui",
  );

  useEffect(() => {
    if (phase !== "reloading") return;
    if (markChunkReloadAttempt()) {
      window.location.reload();
    } else {
      // One-shot, conditional fallback transition — not a render-loop risk:
      // "error-ui" is terminal and this effect no-ops once we're there.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPhase("error-ui");
    }
  }, [phase]);

  const autoReloading = phase === "reloading";

  return { autoReloading, staleDeployError: isStaleDeployError(error) };
}

/**
 * Root-layout listener covering the stale-deploy path error boundaries CANNOT
 * see: a Server Action invoked from an event handler (or a dynamic import
 * awaited in one) rejects outside every boundary, so no error.tsx renders —
 * the click silently does nothing and the user is stuck on the stale build
 * (PostHog issue 019f8e03, UnrecognizedActionError on /coshh). Performs the
 * same one-shot guarded reload as the boundary hook.
 *
 * The reload guard is written in a setTimeout(0) task, NOT synchronously in
 * the listener: PostHog's exception autocapture handles the same
 * `unhandledrejection` dispatch and runs `before_send` synchronously inside
 * it. Deferring the marker write guarantees the suppression predicate
 * (isSuppressedStaleDeployExceptionEvent) evaluates the transient event
 * against "no attempt yet" — and drops it — whichever listener registered
 * first.
 */
export function StaleDeployRejectionRecovery() {
  useEffect(() => {
    function onRejection(event: PromiseRejectionEvent) {
      // Read the reason synchronously; the event object may be reused.
      const reason: unknown = event.reason;
      if (!isStaleDeployError(reason)) return;
      window.setTimeout(() => recoverFromStaleDeployRejection(reason), 0);
    }
    window.addEventListener("unhandledrejection", onRejection);
    return () =>
      window.removeEventListener("unhandledrejection", onRejection);
  }, []);
  return null;
}

/** Calm placeholder rendered for the moment the recovery reload is in flight. */
export function ChunkReloadNotice() {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center px-4 text-center">
      <p className="text-muted">Updating to the latest version&hellip;</p>
    </div>
  );
}

/**
 * Terminal UI for a stale-deploy error that could NOT auto-recover: either the
 * reload-loop guard cannot persist (private mode / storage-blocking policy) or
 * a reload was already attempted and the chunk stayed dead (a proxy/ad-blocker
 * eating a `digitaloceanspaces.com` host, or a genuine CDN posture regression).
 * The generic "Something went wrong" left these users dead-ended (PostHog
 * $exception, one stuck user on /projects 2026-08-04) — give them an explicit
 * reload action instead. A full reload fetches fresh HTML and is the only real
 * fix; `reset()` cannot re-fetch a renamed or blocked chunk.
 *
 * `fullScreen` for the root boundary (which replaces the whole page); the
 * default sits inside an authenticated content area.
 *
 * `escape` MUST carry through whatever navigation link the host boundary
 * offers in its ordinary error UI ("Go to dashboard", "Back to Documents", …).
 * A reload is the only fix for a *stale* chunk, but the other cause of this
 * branch is a chunk the browser will never fetch — a proxy or ad-blocker
 * eating the `digitaloceanspaces.com` host — and there reloading just loops.
 * Replacing the boundary's link with a lone reload button would strand
 * exactly the users this panel exists to rescue.
 *
 * It is a plain `<a>`, deliberately NOT `next/link`: this panel renders only
 * when the JS chunks are known-dead, and a `<Link>` performs a client-side
 * navigation that must load the target route's chunk — the very thing that is
 * broken. An anchor forces a document navigation, which leaves the dead
 * bundle behind. Same reasoning as the reload button next to it.
 */
export function StaleDeployReloadPanel({
  fullScreen = false,
  escape,
}: {
  fullScreen?: boolean;
  escape?: { href: string; label: string };
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center px-4 text-center ${
        fullScreen ? "min-h-screen" : "py-20"
      }`}
    >
      <div className="max-w-md space-y-4">
        <h2 className="text-xl font-semibold text-foreground">
          Couldn&rsquo;t finish loading
        </h2>
        <p className="text-sm text-muted">
          A file this page needs did not load. This is usually temporary —
          reload the page to get the latest version and try again.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-lg bg-accent px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-accent-dark"
          >
            Reload the page
          </button>
          {escape ? (
            <a
              href={escape.href}
              className="inline-flex items-center justify-center rounded-lg border border-border px-6 py-3 text-sm font-medium transition-colors hover:bg-surface"
            >
              {escape.label}
            </a>
          ) : null}
        </div>
      </div>
    </div>
  );
}
